/**
 * C10's seven calls, typed from the generated contract (`KLQ-20260819-004`).
 *
 * THREE THINGS THE CONTRACT SAYS THAT THIS FILE IS BUILT AROUND
 *
 * 1. **None of these lists is paginated.** `MCK-20260812-006` D names a project's team as the
 *    small, stable collection that is the exception; the `Page` envelope is shared so client
 *    code does not fork, but `next_cursor` is always absent. So there is no "load more" on
 *    this screen and no cursor in any query key — and if that ever changes, it changes here
 *    and in the screen together.
 * 2. **Adding an existing account and inviting an address are different acts.**
 *    `POST /members` takes a `user_id` and assigns somebody who already has an account;
 *    `POST /invitations` takes an address, sends mail on the workspace's behalf, and can be
 *    refused by the trust ladder. A person types an email; nobody types a UUID — which is
 *    why the add path needs the workspace member list as its picker.
 * 3. **The invitation response never carries the token**, by design
 *    (`projects/schemas.py:ProjectInvitationResponse`). Nothing here can build an invite link,
 *    and nothing should try: the link goes to one mailbox.
 */

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

type Schemas = components['schemas'];

export type ProjectMember = Schemas['ProjectMemberResponse'];
export type ProjectMemberPage = Schemas['Page_ProjectMemberResponse_'];
export type ProjectRole = ProjectMember['role'];
export type Invitation = Schemas['ProjectInvitationResponse'];
export type InvitationPage = Schemas['Page_ProjectInvitationResponse_'];
export type InvitationStatus = Invitation['status'];
/** A colleague in the caller's own workspace — the add-from-company picker's rows. */
export type WorkspaceMember = Schemas['MemberResponse'];
export type WorkspaceMemberPage = Schemas['Page_MemberResponse_'];

/**
 * The four project roles, in ladder order (`core/levels.py:PROJECT_ROLES`).
 *
 * TWIN: that tuple. There is no `owner` — running the firm is a workspace fact, so a project
 * owner is an `admin` or a `manager` of the owning workspace and holds no project row at all.
 * `design/project/screens/people.jsx` draws one; the backend has never had one.
 */
export const PROJECT_ROLES: readonly ProjectRole[] = [
  'architect',
  'engineer',
  'contractor',
  'client_readonly',
];

export function listMembers(projectId: string): Promise<ProjectMemberPage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/members', {
      params: { path: { project_id: projectId } },
    }),
  );
}

export function listInvitations(projectId: string): Promise<InvitationPage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/invitations', {
      params: { path: { project_id: projectId } },
    }),
  );
}

export type MemberScope = Schemas['MemberScopeResponse'];
export type MemberScopePage = Schemas['Page_MemberScopeResponse_'];

/**
 * Who on this project is limited to which zones — gated on `read_people`, the same authority
 * as the member list (`hierarchy/service.py:read_scope`).
 *
 * **Read here, never written.** A scope is a set of hierarchy node ids, and there is no
 * hierarchy tree in the product yet to pick them from; an editor would be a multi-select of
 * UUIDs. C10 therefore shows what the API reports and leaves the editing to C2, which is the
 * wave that builds the tree (`KLQ-20260819-004`, out of scope).
 */
export function listScopes(projectId: string): Promise<MemberScopePage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/scopes', {
      params: { path: { project_id: projectId } },
    }),
  );
}

// `listWorkspaceMembers` moved to `features/studio/api.ts` on 2026-08-19 with B2, which owns
// the firm's people. The picker reads it through `useWorkspaceMembers` in
// `queries.ts`, so this module keeps only the calls C10 is the sole caller of.

/**
 * Invite an address into this project.
 *
 * No `Idempotency-Key`: `uq_invitation_pending_email` already turns a double-click into a
 * `409` rather than two emails, which is the guarantee the header would otherwise buy
 * (`projects/router.py:333`).
 */
export function sendInvitation(
  projectId: string,
  body: { readonly email: string; readonly role: ProjectRole },
): Promise<Invitation> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/invitations', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/** Withdraw an outstanding invitation. The emailed link stops working immediately. */
export function revokeInvitation(projectId: string, invitationId: string): Promise<unknown> {
  return unwrap(
    api.DELETE('/api/v1/projects/{project_id}/invitations/{invitation_id}', {
      params: { path: { project_id: projectId, invitation_id: invitationId } },
    }),
  );
}

/** Assign an account that already exists. `409` when they are already on the project. */
export function addMember(
  projectId: string,
  body: { readonly user_id: string; readonly role: ProjectRole },
): Promise<ProjectMember> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/members', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

export function changeMemberRole(
  projectId: string,
  membershipId: string,
  role: ProjectRole,
): Promise<ProjectMember> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/members/{membership_id}', {
      params: { path: { project_id: projectId, membership_id: membershipId } },
      body: { role },
    }),
  );
}

export function removeMember(projectId: string, membershipId: string): Promise<unknown> {
  return unwrap(
    api.DELETE('/api/v1/projects/{project_id}/members/{membership_id}', {
      params: { path: { project_id: projectId, membership_id: membershipId } },
    }),
  );
}

/* --- The party roster (`KLQ-20260820-007` wave 1) ------------------------------------ */

/**
 * One organisation on this project, linked to a Kalqa tenant or not.
 *
 * **`workspace_ref_id === null` is the normal state**, not a missing value: the table exists
 * precisely for the electrician who never signs up and the client who works by email. A party
 * is a name the studio asserts and it grants nothing — the spec's §3.
 */
export type ProjectParty = Schemas['ProjectPartyResponse'];
/** A company already on this project, and therefore the only kind that may be linked (§4). */
export type PartyLinkCandidate = Schemas['PartyLinkCandidateResponse'];

/**
 * The roster, by name. Readable by anyone who can read the project.
 *
 * **A plain array, not a `Page`** — unlike everything else C10 reads. No envelope, no cursor;
 * if that ever changes it changes here and in `queries.ts` together.
 *
 * `include_archived` is deliberately not sent: an archived firm is off the job and off every
 * picker, and this list is the picker's only source (spec §2).
 */
export function listParties(projectId: string): Promise<ProjectParty[]> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/parties', {
      params: { path: { project_id: projectId } },
    }),
  );
}

/**
 * Record a firm on this project.
 *
 * **A name is the whole requirement, and creating one sends nothing** — no workspace, no
 * account, no invitation, no mail. That is what the row is for. `409` when the name is taken,
 * case- and whitespace-insensitively, because *Elektro s.r.o.* and *elektro sro* are one firm.
 */
/**
 * Put a firm on this project — **one from the studio's directory, or a new one by name**.
 *
 * `firm_id` names a firm the studio already works with; a bare `name` writes it into the
 * directory first and points the roster row at it, which is how the address book fills up
 * (owner, 2026-09-16). A name the directory already holds is the same firm, not a conflict.
 */
export function createParty(
  projectId: string,
  body: {
    readonly firm_id?: string;
    readonly name?: string;
    readonly note: string | null;
  },
): Promise<ProjectParty> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/parties', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/** Correct a typo, or follow the firm's own renaming. Re-sending the current name converges. */
export function renameParty(
  projectId: string,
  partyId: string,
  name: string,
): Promise<ProjectParty> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/parties/{party_id}', {
      params: { path: { project_id: projectId, party_id: partyId } },
      body: { name },
    }),
  );
}

/**
 * Take a firm out of the pickers without taking it off the work it did.
 *
 * Its own sub-resource rather than a field on the `PATCH`: archiving is a decision about a
 * firm leaving the job, and hiding it in a body that also fixes a typo in the name makes it a
 * change nobody sees happening.
 */
export function archiveParty(projectId: string, partyId: string): Promise<ProjectParty> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/party-archives/{party_id}', {
      params: { path: { project_id: projectId, party_id: partyId } },
    }),
  );
}

/**
 * The companies a party on this project may be linked to: **the people already on the
 * project, and nothing else** (§4).
 *
 * This endpoint is the picker's only source, and that is a rule rather than a convenience.
 * Deriving the list from the member query would put the filter in the client; a picker fed by
 * anything wider — a workspace search, a typed id — is a directory of every tenant in the
 * system handed to anybody who can edit a project. The server refuses an arbitrary id anyway
 * (`link_party_to_workspace`); the picker not offering one is the other enforcement point of
 * the same rule (retro rule 1).
 */
export function listPartyLinkCandidates(projectId: string): Promise<PartyLinkCandidate[]> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/party-link-candidates', {
      params: { path: { project_id: projectId } },
    }),
  );
}

/**
 * Record that this party turned out to be that tenant. **Once, and never undone.**
 *
 * There is no `DELETE` on this path and there will not be one: a mistaken link is corrected by
 * archiving the party and recording another, so both facts stay readable on the work they were
 * on. Linking admits the firm to every task the party is assigned, dated at the link — which
 * is why the caller confirms it before it happens (§4).
 */
export function linkParty(
  projectId: string,
  partyId: string,
  workspaceId: string,
): Promise<ProjectParty> {
  return unwrap(
    api.PUT('/api/v1/projects/{project_id}/party-links/{party_id}', {
      params: { path: { project_id: projectId, party_id: partyId } },
      body: { workspace_id: workspaceId },
    }),
  );
}

/* --- Contacts and roles (`KLQ-20260820-007` wave 2) ---------------------------------- */

/**
 * A named person at a firm on this project — the kontakt list's *meno · telefón · mail*.
 *
 * **A record, never a channel** (§3). `email` is printed on this screen and nothing in the
 * system mails it; a contact holds no account and no membership, and creating one invites
 * nobody.
 */
export type PartyContact = Schemas['PartyContactResponse'];

/**
 * One entry of the project's statutory-role vocabulary — *Zhotoviteľ*, *HAP*.
 *
 * **Not `ProjectRole`**, which above is the *membership* role and which every current importer
 * means. The two are unrelated — one is what a person may do here, the other the office a firm
 * holds on the building — and the backend named its schema `…Entry…` for the same collision.
 */
export type ProjectRoleEntry = Schemas['ProjectRoleEntryResponse'];

/**
 * Every contact on the project, **for every firm at once**.
 *
 * The endpoint takes a `party_id` filter and this deliberately does not send it: the section
 * draws every party's contacts on one screen, and a read per party is a request per row
 * (CLAUDE.md, data fetching). The rows are grouped by `project_party_id` in the client.
 */
export function listPartyContacts(projectId: string): Promise<PartyContact[]> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/party-contacts', {
      params: { path: { project_id: projectId } },
    }),
  );
}

/** Add a person to a firm. One row and nothing else — no account, no invitation, no mail. */
export function createPartyContact(
  projectId: string,
  body: {
    readonly project_party_id: string;
    readonly name: string;
    readonly phone: string | null;
    readonly email: string | null;
  },
): Promise<PartyContact> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/party-contacts', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/** Remove a contact outright. No archive, nothing to redact; repeating it is a `204` too. */
export function deletePartyContact(projectId: string, contactId: string): Promise<unknown> {
  return unwrap(
    api.DELETE('/api/v1/projects/{project_id}/party-contacts/{contact_id}', {
      params: { path: { project_id: projectId, contact_id: contactId } },
    }),
  );
}

/**
 * The whole role vocabulary, **archived entries included**.
 *
 * One read serves two needs: a party's chips must still name a role that was retired or
 * narrowed to `person` after the firm was recorded under it, and the picker offers only live
 * `party`/`both` entries. `picker=party` would answer the second and break the first, so the
 * picker's filter is applied over this list in `PartySection` — and `PUT /party-roles` refuses
 * a `person` entry anyway, which is the enforcement point that binds (retro rule 1).
 */
export function listProjectRoles(projectId: string): Promise<ProjectRoleEntry[]> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/roles', {
      params: { path: { project_id: projectId }, query: { include_archived: true } },
    }),
  );
}

/** The whole set of roles a firm holds, stated rather than added to. */
export function setPartyRoles(
  projectId: string,
  partyId: string,
  roleIds: readonly string[],
): Promise<ProjectParty> {
  return unwrap(
    api.PUT('/api/v1/projects/{project_id}/party-roles/{party_id}', {
      params: { path: { project_id: projectId, party_id: partyId } },
      body: { role_ids: [...roleIds] },
    }),
  );
}
